import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BalanceSheet.css';
import baseURL from '../../../../Url/NodeBaseURL';

const BalanceSheet = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [balanceSheetData, setBalanceSheetData] = useState(null);
  const [asOnDate, setAsOnDate] = useState('');

  // Fetch Balance Sheet data
  const fetchBalanceSheet = async () => {
    if (!asOnDate) {
      alert('Please select a date');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${baseURL}/ledger/balance-sheet`, {
        params: { as_on_date: asOnDate }
      });

      console.log('Balance Sheet Response:', response.data);

      if (response.data.success) {
        setBalanceSheetData(response.data);
      } else {
        setError(response.data.message || 'Failed to fetch Balance Sheet data');
      }
    } catch (err) {
      console.error('Error fetching Balance Sheet:', err);
      setError(err.response?.data?.error || 'Failed to fetch Balance Sheet data');
    } finally {
      setLoading(false);
    }
  };

  // Handle date change
  const handleDateChange = (e) => {
    setAsOnDate(e.target.value);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '₹0.00';
    return `₹${Number(amount).toFixed(2)}`;
  };

  // Set default date to today
  useEffect(() => {
    const today = new Date();
    setAsOnDate(today.toISOString().split('T')[0]);
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    if (asOnDate) {
      fetchBalanceSheet();
    }
  }, []);

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bs-container">
      <div className="bs-header">
        <div className="bs-header-top">
          <h2>📋 Balance Sheet</h2>
          <div className="bs-header-actions">
            <button onClick={fetchBalanceSheet} className="btn-refresh" disabled={loading}>
              🔄 Refresh
            </button>
            <button onClick={handlePrint} className="btn-print">
              🖨️ Print
            </button>
          </div>
        </div>
        <div className="bs-controls">
          <div className="date-picker">
            <label>As On:</label>
            <input
              type="date"
              value={asOnDate}
              onChange={handleDateChange}
              className="date-input"
            />
            <button 
              onClick={fetchBalanceSheet}
              className="btn-generate"
              disabled={loading}
            >
              {loading ? '⏳ Loading...' : '📊 Generate'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bs-error">
          <span className="error-icon">❌</span>
          {error}
        </div>
      )}

      {loading ? (
        <div className="bs-loading">
          <div className="loader"></div>
          <p>Generating Balance Sheet...</p>
        </div>
      ) : balanceSheetData ? (
        <div className="bs-content" id="print-area">
          <div className="bs-report-header">
            <h3>Balance Sheet</h3>
            <p className="bs-date">
              As on {new Date(balanceSheetData.as_on_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          </div>

          <div className="bs-grid">
            {/* Liabilities Side */}
            <div className="bs-side bs-liabilities">
              <h4 className="section-title liability-title">Liabilities</h4>
              <table className="bs-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {balanceSheetData.liabilities.details && balanceSheetData.liabilities.details.length > 0 ? (
                    balanceSheetData.liabilities.details.map((item, index) => (
                      <tr key={index}>
                        <td className="item-name">{item.account_name}</td>
                        <td className="item-amount text-right">{formatCurrency(item.balance)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="item-name">No Liabilities</td>
                      <td className="item-amount text-right">₹0.00</td>
                    </tr>
                  )}
                  <tr className="total-row">
                    <td className="total-label"><strong>Total Liabilities</strong></td>
                    <td className="total-amount text-right"><strong>{formatCurrency(balanceSheetData.liabilities.total)}</strong></td>
                  </tr>
                </tbody>
              </table>

              <h4 className="section-title equity-title" style={{ marginTop: '25px' }}>Equity</h4>
              <table className="bs-table">
                <tbody>
                  <tr>
                    <td className="item-name">Owner's Capital</td>
                    <td className="item-amount text-right">{formatCurrency(balanceSheetData.equity.total)}</td>
                  </tr>
                  <tr>
                    <td className="item-name">Add: Net Profit</td>
                    <td className={`item-amount text-right ${balanceSheetData.equity.net_profit >= 0 ? 'profit-positive' : 'profit-negative'}`}>
                      {formatCurrency(balanceSheetData.equity.net_profit)}
                    </td>
                  </tr>
                  <tr className="total-row">
                    <td className="total-label"><strong>Total Equity</strong></td>
                    <td className="total-amount text-right"><strong>{formatCurrency(balanceSheetData.equity.total_equity)}</strong></td>
                  </tr>
                </tbody>
              </table>

              <div className="bs-total-box">
                <div className="total-box-label">Total Liabilities & Equity</div>
                <div className="total-box-amount">{formatCurrency(balanceSheetData.verification.total_liabilities_equity)}</div>
              </div>
            </div>

            {/* Assets Side */}
            <div className="bs-side bs-assets">
              <h4 className="section-title asset-title">Assets</h4>
              <table className="bs-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {balanceSheetData.assets.details && balanceSheetData.assets.details.length > 0 ? (
                    balanceSheetData.assets.details.map((item, index) => (
                      <tr key={index}>
                        <td className="item-name">{item.account_name}</td>
                        <td className="item-amount text-right">{formatCurrency(item.balance)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="item-name">No Assets</td>
                      <td className="item-amount text-right">₹0.00</td>
                    </tr>
                  )}
                  <tr className="total-row">
                    <td className="total-label"><strong>Total Assets</strong></td>
                    <td className="total-amount text-right"><strong>{formatCurrency(balanceSheetData.assets.total)}</strong></td>
                  </tr>
                </tbody>
              </table>

              <div className="bs-total-box">
                <div className="total-box-label">Total Assets</div>
                <div className="total-box-amount">{formatCurrency(balanceSheetData.assets.total)}</div>
              </div>
            </div>
          </div>

          {/* Verification */}
          <div className={`bs-verification ${balanceSheetData.verification.is_balanced ? 'balanced' : 'unbalanced'}`}>
            {balanceSheetData.verification.is_balanced ? (
              <>
                <span className="verification-icon">✅</span>
                <span>Balance Sheet is Balanced</span>
              </>
            ) : (
              <>
                <span className="verification-icon">⚠️</span>
                <span>Balance Sheet is NOT Balanced (Difference: {formatCurrency(balanceSheetData.verification.difference)})</span>
              </>
            )}
          </div>

          {/* Summary Cards */}
          <div className="bs-summary">
            <div className="summary-card">
              <span className="summary-label">Total Assets</span>
              <span className="summary-value">{formatCurrency(balanceSheetData.assets.total)}</span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Total Liabilities</span>
              <span className="summary-value">{formatCurrency(balanceSheetData.liabilities.total)}</span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Total Equity</span>
              <span className="summary-value profit-positive">{formatCurrency(balanceSheetData.equity.total_equity)}</span>
            </div>
            <div className="summary-card highlight">
              <span className="summary-label">Net Worth</span>
              <span className="summary-value">{formatCurrency(balanceSheetData.equity.total_equity)}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="bs-no-data">
          <span className="no-data-icon">📭</span>
          <p>No data available. Please generate the report.</p>
        </div>
      )}
    </div>
  );
};

export default BalanceSheet;